// Centralized data fetching and storage for daily digest data.
// This composable handles the raw API data - transformations happen in other composables.

import { ref } from '@vue/composition-api';
import { fetchDailyDigest } from '../components/APIManager.js';

/**
 * Initial/default structure for daily digest data.
 * Used to initialize the dailyDigest reactive state with empty/default values.
 */
const initialDailyDigest = {
  summary: {},
  expiring_contracts: [],
  competitor_activity: [],
  competitor_expiring_contracts: [],
  buyer_activity: [],
  market_patterns: [],
  market_signals: [],
  new_matches: [],
  saved_search_new_matches: [],
  saved_searches_new_awarded_contracts: [],
  industry_matches: [],
  new_entrants: [],
  panel_intelligence: {
    competitors_recently_joined: [],
    suggested_panels: [],
    newly_created_panels: []
  }
};

/**
 * Composable for fetching and storing daily digest data.
 * This is the single source of truth for raw daily digest data.
 * 
 * @returns {Object} Object containing reactive state and methods
 */
export function useDailyDigest() {
  // Reactive state for raw daily digest data
  const dailyDigest = ref({ ...initialDailyDigest });
  // Loading state indicator for data fetching
  const loading = ref(false);

  /**
   * Fetches daily digest data from the API.
   * Handles different response structures and updates the dailyDigest state.
   * Sets loading state during the fetch operation.
   * @returns {Promise<void>}
   */
  const load = async () => {
    loading.value = true;
    try {
      const response = await fetchDailyDigest();
      // API sometimes comes back as { status, data: {...} } and sometimes just the payload.
      const payload = (response && response.data && response.data.data) ||
        (response && response.data) ||
        response;
      if (payload && typeof payload === 'object') {
        const savedSearchMatches =
          payload.saved_search_new_matches ||
          payload.saved_searches_new_awarded_contracts ||
          [];
        dailyDigest.value = {
          ...dailyDigest.value,
          ...payload,
          competitor_activity: payload.competitor_activity || [],
          competitor_expiring_contracts: payload.competitor_expiring_contracts || [],
          saved_search_new_matches: savedSearchMatches,
          saved_searches_new_awarded_contracts: savedSearchMatches,
          panel_intelligence: payload.panel_intelligence || {
            competitors_recently_joined: [],
            suggested_panels: [],
            newly_created_panels: []
          }
        };
      }
    } catch (error) {
      // console.error('Error fetching daily digest:', error);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Resets the daily digest data to initial state.
   * Useful for clearing data when needed.
   */
  const reset = () => {
    dailyDigest.value = { ...initialDailyDigest };
  };

  return {
    // Reactive state
    dailyDigest,
    loading,
    // Methods
    load,
    reset
  };
}
